import React, { useState } from "react";
import { Button, Col, Form, message, Row, Skeleton, Table } from "antd";
import { CheckOutlined, EyeOutlined, PlusOutlined } from "@ant-design/icons";
import moment from "moment";
import { useParams } from "react-router-dom";

import { useFetchConstant } from "apis/constant";
import {
  patchAdvisee,
  patchAddFamilyMember,
  patchUpdateFamilyMember,
  patchRemoveFamilyMember,
} from "apis/advisee";

import FormToggle from "components/FormToggle/FormToggle";
import SectionCard from "components/SectionCard/SectionCard";
import FormDatePicker from "components/FormDatePicker/FormDatePicker";
import FormInput from "components/FormInput/FormInput";
import FormGroupSelectionInput from "components/FormGroupSelectionInput/FormGroupSelectionInput";
import ScrollableContainer from "components/ScrollableContainer/ScrollableContainer";
import SectionContainer from "components/SectionContainer/SectionContainer";
import FormSelection from "components/FormSelection/FormSelection";
import Title from "components/Title/Title";
import Tips from "components/Tips/Tips";

import { getLabelOfConstant } from "utils/general";
import { buildPPISummaryUri } from "utils/routes";

import AddFamilyMemberModal from "./components/AddFamilyMemberModal/AddFamilyMemberModal";
import { Label } from "./PersonalDetails.styles";
import Locale from "react-intl-universal";

const { useForm } = Form;

const formatFormInitialValues = (defaultValues, adviseeCategoriesConst) => ({
  ...defaultValues,
  dob: defaultValues.dob ? moment(defaultValues.dob, "YYYY-MM-DD") : undefined,
  contact: {
    countryCode: "+60",
    ...defaultValues.contact,
  },
  isClient: {
    value: defaultValues.category === adviseeCategoriesConst.CLIENT.code,
  },
  joinDate: defaultValues.joinDate
    ? moment(defaultValues.joinDate, "YYYY-MM-DD")
    : moment(),
  medicalHealthInsuranceNeeds: defaultValues.medicalHealthInsuranceNeeds
    ? defaultValues.medicalHealthInsuranceNeeds
    : 200000,
});

const PersonalDetails = ({ personalInfo = {} }) => {
  console.log("personalInfo", personalInfo);

  const { id: adviseeId } = useParams();
  const [form] = useForm();
  const {
    isLoading: isAdviseeCategoriesLoading,
    data: adviseeCategoriesConst,
  } = useFetchConstant("adviseeCategories");
  const {
    isLoading: isRelationshipsLoading,
    selection: relationships,
  } = useFetchConstant("relationships");
  const {
    isLoading: isCountryCodesLoading,
    selection: countryCodes,
  } = useFetchConstant("countries", "phoneCode", "phoneCode");
  const [familyMembers, setFamilyMembers] = useState(
    personalInfo.familyMembers
  );
  const [showFamilyMemberModal, setShowFamilyMemberModal] = useState(false);
  const [selectedFamilyMember, setSelectedFamilyMember] = useState({});

  const isLoading =
    isRelationshipsLoading ||
    isCountryCodesLoading ||
    isAdviseeCategoriesLoading;

  const handleOnFamilyMemberRowSelect = (selectedFamilyMember) => {
    setShowFamilyMemberModal(true);
    setSelectedFamilyMember(selectedFamilyMember);
  };

  const handleOnAddFamilyMemberModalOkButtonClick = (newFamilyMember) => {
    if (Object.keys(selectedFamilyMember).length) {
      patchUpdateFamilyMember(
        adviseeId,
        selectedFamilyMember._id,
        newFamilyMember
      ).then((updatedFamilyMember) => {
        const newFamilyMembers = familyMembers.map((member) =>
          member._id === selectedFamilyMember._id ? updatedFamilyMember : member
        );

        setFamilyMembers(newFamilyMembers);
        setShowFamilyMemberModal(false);
        setSelectedFamilyMember({});
      });
    } else {
      patchAddFamilyMember(adviseeId, newFamilyMember).then(
        (addedFamilyMember) => {
          setFamilyMembers([...familyMembers, addedFamilyMember]);
          setShowFamilyMemberModal(false);
        }
      );
    }
  };

  const handleOnAddFamilyMemberModalRemoveButtonClick = () => {
    patchRemoveFamilyMember(adviseeId, selectedFamilyMember._id).then(() => {
      const newFamilyMembers = familyMembers.filter(
        (member) => member._id !== selectedFamilyMember._id
      );
      setFamilyMembers(newFamilyMembers);
      setShowFamilyMemberModal(false);
      setSelectedFamilyMember({});
    });
  };

  const handleOnAddFamilyMemberModalCloseButtonClick = () => {
    setShowFamilyMemberModal(false);
    setSelectedFamilyMember({});
  };

  const handleOnSaveButtonClick = async (e) => {
    e.preventDefault();
    try {
      const values = await form.validateFields();
      const payload = {
        ...values,
        dob: values.dob && moment(values.dob).format("YYYY-MM-DD"),
        joinDate:
          values.joinDate && moment(values.joinDate).format("YYYY-MM-DD"),
        category: values.isClient.value
          ? adviseeCategoriesConst.CLIENT.code
          : adviseeCategoriesConst.PROSPECT.code,
        yearsOfCICoverage: values.yearsOfCICoverage && {
          min: Number(values.yearsOfCICoverage.min),
          max: Number(values.yearsOfCICoverage.max),
        },
        yearsOfTPDCoverage: values.yearsOfTPDCoverage && {
          min: Number(values.yearsOfTPDCoverage.min),
          max: Number(values.yearsOfTPDCoverage.max),
        },
      };
      patchAdvisee(adviseeId, payload)
        .then(() => {
          message.success(Locale.get("personalDetail.updateSuccess"));
        })
        .catch((ex) => {
          message.error(Locale.get("personalDetail.errorUpdateAdviseeDetails"));
        });
    } catch (error) {
      error.errorFields.forEach((field) => message.error(field.errors[0]));
    }
  };

  const getAccountActivationRender = (refAdviseeId) => {
    if (refAdviseeId) {
      return (
        <Button
          icon={<EyeOutlined />}
          onClick={(e) => {
            e.stopPropagation();
            const win = window.open(buildPPISummaryUri(refAdviseeId), "_blank");
            win.focus();
          }}
        >
          {Locale.get("personalDetail.viewProfile")}
        </Button>
      );
    }
  };

  const columns = [
    {
      title: Locale.get("personalDetail.familyMember.fullName"),
      dataIndex: "name",
      key: "name",
      render: (value) => (
        <span>{`${value.firstName || ""} ${value.lastName || ""}` || "-"}</span>
      ),
    },
    {
      title: Locale.get("personalDetail.familyMember.dob"),
      dataIndex: "dob",
      key: "dob",
      render: (value) => <span>{value || "-"}</span>,
    },
    {
      title: Locale.get("personalDetail.familyMember.relationship"),
      dataIndex: "relationship",
      key: "relationship",
      render: (value) => (
        <span>{getLabelOfConstant(value, relationships) || "-"}</span>
      ),
    },
    {
      title: Locale.get("personalDetail.familyMember.yearOfIndependence"),
      dataIndex: "yearOfIndependence",
      key: "yearOfIndependence",
      render: (value) => (
        <span>
          {value || <i>{Locale.get("personalDetail.notDependent")}</i>}
        </span>
      ),
    },
    {
      title: Locale.get("personalDetail.familyMember.action"),
      dataIndex: "refAdviseeId",
      key: "refAdviseeId",
      render: getAccountActivationRender,
    },
  ];

  // const tipsDescription =
  // <p>
  //   Years of coverage for basic responsibility in{" "}
  //   <b>Total Permanent Disability (TPD)</b> and <b>Critical Illness (CI)</b>{" "}
  //   will be used to calculate the insurance needs coverage.{" "}
  //   <b>Immediate Death Expenses (IDE)</b> will be used in that calculation as
  //   well.
  // </p>

  return (
    <>
      {isLoading ? (
        <Skeleton active />
      ) : (
        <>
          <Row justify="end" style={{ marginBottom: "8px" }}>
            <Button
              type="primary"
              icon={<CheckOutlined />}
              onClick={handleOnSaveButtonClick}
            >
              {Locale.get("personalDetail.saveButton")}
            </Button>
          </Row>
          <ScrollableContainer>
            <Form
              form={form}
              initialValues={formatFormInitialValues(
                personalInfo,
                adviseeCategoriesConst
              )}
            >
              <Row
                justify="space-between"
                align="middle"
                style={{ marginBottom: "32px" }}
              >
                <Title>{Locale.get("personalDetail.pageTitle")}</Title>
              </Row>
              <SectionContainer>
                <Row gutter={16}>
                  <Col span={12}>
                    <FormInput
                      label={Locale.get("personalDetail.fName")}
                      name={["name", "firstName"]}
                      placeholder="John"
                      requiredErrorMessage={Locale.get(
                        "personalDetail.errMsgFName"
                      )}
                    />
                  </Col>
                  <Col span={12}>
                    <FormInput
                      label={Locale.get("personalDetail.lName")}
                      name={["name", "lastName"]}
                      placeholder="Doe"
                      requiredErrorMessage={Locale.get(
                        "personalDetail.errMsgLName"
                      )}
                    />
                  </Col>
                  <Col span={12}>
                    <FormInput
                      label={Locale.get("personalDetail.idPassportNo")}
                      name="nric"
                      placeholder={Locale.get(
                        "personalDetail.idPassportNoPlaceholder"
                      )}
                      requiredErrorMessage={Locale.get(
                        "personalDetail.errMsgIdPassport"
                      )}
                      mandatory={false}
                    />
                  </Col>

                  <Col span={12}>
                    <FormDatePicker
                      label={Locale.get("personalDetail.dob")}
                      name="dob"
                      placeholder="1994-01-01"
                      requiredErrorMessage={Locale.get(
                        "personalDetail.errMsgDob"
                      )}
                      isDob
                    />
                  </Col>
                  <Col span={12}>
                    <FormInput
                      label={Locale.get("personalDetail.occupation")}
                      name="occupation"
                      placeholder={Locale.get("personalDetail.optional")}
                    />
                  </Col>
                  <Col span={12}>
                    <FormSelection
                      label={Locale.get("personalDetail.gender")}
                      name="gender"
                      placeholder={Locale.get("personalDetail.gender")}
                      mandatory={false}
                      requiredErrorMessage={Locale.get(
                        "personalDetail.errMsgGenderAndMs"
                      )}
                      selections={[
                        {
                          label: Locale.get("personalDetail.maleLabel"),
                          value: "Male",
                        },
                        {
                          label: Locale.get("personalDetail.femaleLabel"),
                          value: "Female",
                        },
                        {
                          label: "N/A",
                          value: "N/A",
                        },
                      ]}
                    />
                  </Col>

                  <Col span={12}>
                    <FormGroupSelectionInput
                      label={Locale.get("personalDetail.contactNo")}
                      inputName={["contact", "contactNumber"]}
                      selectionName={["contact", "countryCode"]}
                      inputPlaceholder={Locale.get("personalDetail.optional")}
                      inputType="number"
                      selections={countryCodes}
                    />
                  </Col>
                  <Col span={12}>
                    <FormInput
                      label={Locale.get("personalDetail.email")}
                      name="email"
                      placeholder={Locale.get("personalDetail.optional")}
                      type="email"
                    />
                  </Col>
                  <Col span={12}>
                    <FormSelection
                      name="marital_status"
                      label={Locale.get("personalDetail.maritalStatus")}
                      placeholder={Locale.get("personalDetail.maritalStatus")}
                      mandatory={false}
                      requiredErrorMessage={Locale.get(
                        "personalDetail.errMsgGenderAndMs"
                      )}
                      selections={[
                        {
                          label: Locale.get("personalDetail.singleLabel"),
                          value: "Single",
                        },
                        {
                          label: Locale.get("personalDetail.marriedLabel"),
                          value: "Married",
                        },
                        {
                          label: Locale.get("personalDetail.divorcedLabel"),
                          value: "Divorced",
                        },
                        {
                          label: Locale.get("personalDetail.widowedLabel"),
                          value: "Widowed",
                        },
                        {
                          label: Locale.get("personalDetail.othersLabel"),
                          value: "Other",
                        },
                      ]}
                    />
                  </Col>

                  <Form.Item noStyle shouldUpdate>
                    {(e) => {
                      const marital_status = e.getFieldValue("marital_status");

                      console.log("marital_status", marital_status);

                      if (marital_status === "Other") {
                        return (
                          <Col span={12}>
                            <FormInput
                              label={Locale.get("personalDetail.mso")}
                              name="marital_status_other"
                              placeholder="Other"
                              requiredErrorMessage="Please enter Marital Status Other"
                            />
                          </Col>
                        );
                      }
                    }}
                  </Form.Item>
                  <Col span={12}>
                    <FormSelection
                      name="isSmoker"
                      label={Locale.get("personalDetail.smokerStatus")}
                      placeholder={Locale.get("personalDetail.smokerStatus")}
                      mandatory={true}
                      requiredErrorMessage={Locale.get(
                        "personalDetail.errMsgSmokerStatus"
                      )}
                      selections={[
                        {
                          label: Locale.get("personalDetail.smokerYes"),
                          value: true,
                        },
                        {
                          label: Locale.get("personalDetail.smokerNo"),
                          value: false,
                        },
                      ]}
                    />
                  </Col>

                  <Col span={12}>
                    <FormInput
                      label={Locale.get("personalDetail.remarks")}
                      name="remarks"
                      placeholder={Locale.get("personalDetail.optional")}
                    />
                  </Col>
                  <Col span={24}>
                    <FormToggle
                      label={Locale.get("personalDetail.toggleFormLabel")}
                      name="isClient"
                    >
                      <SectionCard
                        title={Locale.get("personalDetail.sectionCardTitle")}
                      >
                        <FormDatePicker name="joinDate" />
                      </SectionCard>
                    </FormToggle>
                  </Col>
                </Row>
              </SectionContainer>
              <SectionContainer>
                <Row
                  justify="space-between"
                  align="middle"
                  style={{ marginBottom: "16px", width: "100%" }}
                >
                  <Title>
                    {Locale.get(
                      "personalDetail.familyMember.sectionContainerTitle_0"
                    )}
                  </Title>
                  <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    onClick={() => setShowFamilyMemberModal(true)}
                  >
                    {Locale.get("personalDetail.addNewMemberButton")}
                  </Button>
                </Row>
                <Row style={{ width: "100%" }}>
                  <Table
                    style={{ width: "100%" }}
                    pagination={false}
                    rowKey={(record) => record.name}
                    dataSource={familyMembers}
                    columns={columns}
                    onRow={(record) => {
                      return {
                        onClick: () => handleOnFamilyMemberRowSelect(record),
                      };
                    }}
                  />
                </Row>
              </SectionContainer>
              <SectionContainer>
                <Title marginBottom="16px">
                  {Locale.get("personalDetail.rci.sectionContainerTitle_1")}
                </Title>
                <Row>
                  <Col span={12}>
                    <Row gutter={16}>
                      <Col span={24}>
                        <Label>
                          {Locale.get("personalDetail.rci.tpdLabel")}
                        </Label>
                      </Col>
                      <Col span={12}>
                        <FormInput
                          label={Locale.get("personalDetail.rci.minYear")}
                          name={["yearsOfTPDCoverage", "min"]}
                          placeholder="10"
                          suffix={Locale.get("personalDetail.rci.yearSuffix")}
                          extraProps={{
                            type: "number",
                          }}
                          requiredErrorMessage={Locale.get(
                            "personalDetail.rci.errMsgTpdMin"
                          )}
                        />
                      </Col>
                      <Col span={12}>
                        <FormInput
                          label={Locale.get("personalDetail.rci.maxYear")}
                          name={["yearsOfTPDCoverage", "max"]}
                          placeholder="20"
                          suffix={Locale.get("personalDetail.rci.yearSuffix")}
                          extraProps={{
                            type: "number",
                          }}
                          requiredErrorMessage={Locale.get(
                            "personalDetail.rci.errMsgTpdMax"
                          )}
                        />
                      </Col>
                    </Row>
                    <Row gutter={16}>
                      <Col span={24}>
                        <Label>
                          {Locale.get("personalDetail.rci.ciLabel")}
                        </Label>
                      </Col>
                      <Col span={12}>
                        <FormInput
                          label={Locale.get("personalDetail.rci.minYear")}
                          name={["yearsOfCICoverage", "min"]}
                          placeholder="3"
                          suffix={Locale.get("personalDetail.rci.yearSuffix")}
                          extraProps={{
                            type: "number",
                          }}
                          requiredErrorMessage={Locale.get(
                            "personalDetail.rci.errMsgCiMin"
                          )}
                        />
                      </Col>
                      <Col span={12}>
                        <FormInput
                          label={Locale.get("personalDetail.rci.maxYear")}
                          name={["yearsOfCICoverage", "max"]}
                          placeholder="5"
                          suffix={Locale.get("personalDetail.rci.yearSuffix")}
                          extraProps={{
                            type: "number",
                          }}
                          requiredErrorMessage={Locale.get(
                            "personalDetail.rci.errMsgTpdMax"
                          )}
                        />
                      </Col>
                    </Row>
                    <Row gutter={16}>
                      <Col span={24}>
                        <FormInput
                          label={Locale.get("personalDetail.rci.ideLabel")}
                          name="immediateDeathAmt"
                          placeholder="e.g. 58,000.00"
                          type="financial"
                          requiredErrorMessage="Please enter an amount of IDE."
                        />
                      </Col>
                    </Row>
                    <Row gutter={16}>
                      <Col span={24}>
                        <FormInput
                          label={Locale.get("personalDetail.rci.rrfLabel")}
                          name="recoverRecuperatingFund"
                          placeholder="e.g. 10,000.00"
                          type="financial"
                        />
                      </Col>
                    </Row>
                    <Row gutter={16}>
                      <Col span={24}>
                        <FormInput
                          label={Locale.get("personalDetail.rci.medicalLabel")}
                          name="medicalHealthInsuranceNeeds"
                          placeholder="e.g. 200,000.00"
                          type="financial"
                          requiredErrorMessage="Please enter Medical Health Insurance Needs"
                        />
                      </Col>
                    </Row>
                  </Col>
                  <Col span={12}>
                    <Tips
                      title={Locale.get("personalDetail.tips")}
                      description={Locale.get("personalDetail.tipsDescription")}
                    />
                  </Col>
                </Row>
              </SectionContainer>
            </Form>
          </ScrollableContainer>
        </>
      )}
      {showFamilyMemberModal && (
        <AddFamilyMemberModal
          defaultValues={selectedFamilyMember}
          onClose={handleOnAddFamilyMemberModalCloseButtonClick}
          onOk={handleOnAddFamilyMemberModalOkButtonClick}
          onRemove={handleOnAddFamilyMemberModalRemoveButtonClick}
          visible={showFamilyMemberModal}
          adviseeId={adviseeId}
        />
      )}
    </>
  );
};

export default PersonalDetails;
